/**
 * Pure week-math for the Ukeplanlegger (weekly meal planner, Slice 3).
 *
 * CRITICAL: every date here is built from a `Date`'s LOCAL calendar fields
 * (`getFullYear`/`getMonth`/`getDate`) and formatted by hand — NEVER via
 * `toISOString()`, which converts to UTC first and can shift the calendar
 * date by one day for a caller east/west of UTC (e.g. a Norwegian user just
 * after midnight local time). This module never calls `toISOString`.
 *
 * "A week" is always Monday..Sunday, identified by that week's Monday as a
 * `YYYY-MM-DD` string — this MUST exactly match what the backend expects as
 * `weekStart` (`GET .../meal-plan?weekStart=...`), which 400s on anything
 * that isn't a Monday.
 */

const DAY_NAMES = ["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"] as const;
const MONTH_NAMES = [
  "jan",
  "feb",
  "mar",
  "apr",
  "mai",
  "jun",
  "jul",
  "aug",
  "sep",
  "okt",
  "nov",
  "des",
] as const;

/** Norwegian weekday abbreviations, Monday-first (index 0 = Man). */
export const WEEKDAY_NAMES = DAY_NAMES;

export interface WeekDay {
  /** That day's calendar date, `YYYY-MM-DD`, LOCAL — never UTC-derived. */
  date: string;
  /** 0 = Monday .. 6 = Sunday. */
  dayIndex: number;
  /** Norwegian abbreviation ("Man", "Tir", …). */
  label: string;
  /** True when `date` is the caller's local "today". */
  isToday: boolean;
}

export interface WeekInfo {
  /** That week's Monday, `YYYY-MM-DD` — the exact value to send as `weekStart`. */
  monday: string;
  /** The 7 days of the week, Monday first. */
  days: WeekDay[];
  /** Norwegian range label, e.g. "3.–9. mars" or "28. feb–6. mars". */
  rangeLabel: string;
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** Format a `Date`'s LOCAL calendar fields as `YYYY-MM-DD` — never `toISOString()`. */
export function toLocalDateString(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Midnight-local `Date` for a given local `Date`'s calendar day (strips time-of-day). */
function localMidnight(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * The Monday (as a local midnight `Date`) of the week `weekOffset` weeks away
 * from the week containing `reference` (0 = the week containing `reference`,
 * 1 = next week, -1 = last week, …). Entirely local-calendar arithmetic.
 */
export function mondayOfWeek(reference: Date, weekOffset = 0): Date {
  const day = reference.getDay(); // 0 = Sun .. 6 = Sat
  const diffToMonday = day === 0 ? -6 : 1 - day;
  return new Date(
    reference.getFullYear(),
    reference.getMonth(),
    reference.getDate() + diffToMonday + weekOffset * 7,
  );
}

/** That week's Monday as a `YYYY-MM-DD` string — the exact `weekStart` contract value. */
export function mondayString(weekOffset: number, now: Date = new Date()): string {
  return toLocalDateString(mondayOfWeek(now, weekOffset));
}

/**
 * Norwegian weekday abbreviation ("Man", "Tir", …) for an absolute
 * `YYYY-MM-DD` date — used by the drag-and-drop screen-reader announcements
 * (Ukeplanlegger, Slice 4). Parses the Y/M/D components directly rather than
 * via `new Date(dateString)`, which JS treats a date-only ISO string as
 * UTC-midnight and could shift the weekday by one day near a timezone
 * boundary — same rule as the rest of this module (see header docs).
 */
export function weekdayLabel(date: string): string {
  const [y, m, d] = date.split("-").map(Number);
  const local = new Date(y!, (m ?? 1) - 1, d ?? 1);
  return DAY_NAMES[(local.getDay() + 6) % 7]!; // getDay(): 0=Sun..6=Sat → reindex 0=Mon..6=Sun
}

/** The absolute calendar date (`YYYY-MM-DD`) for `dayIndex` (0=Mon..6=Sun) within `weekOffset`. */
export function dateForDayInWeek(
  weekOffset: number,
  dayIndex: number,
  now: Date = new Date(),
): string {
  const monday = mondayOfWeek(now, weekOffset);
  const d = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + dayIndex);
  return toLocalDateString(d);
}

/** "3.–9. mars" within one month, or "28. feb–6. mars" across a month boundary. */
function formatWeekRange(monday: Date): string {
  const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);
  if (monday.getMonth() === sunday.getMonth()) {
    return `${monday.getDate()}.–${sunday.getDate()}. ${MONTH_NAMES[monday.getMonth()]}`;
  }
  return `${monday.getDate()}. ${MONTH_NAMES[monday.getMonth()]}–${sunday.getDate()}. ${MONTH_NAMES[sunday.getMonth()]}`;
}

/**
 * Norwegian range label ("3.–9. mars" / "28. feb–6. mars") for the week
 * identified by its Monday, given only that Monday as a `YYYY-MM-DD` string —
 * for a caller (e.g. `ShoppingListOverlay`) that only has `weekStart` on hand,
 * not the `weekOffset`/`now` pair `getWeek` needs. Parses the Y/M/D
 * components directly rather than `new Date(dateString)` — same UTC-shift
 * rule as `weekdayLabel` above.
 */
export function rangeLabelForMonday(monday: string): string {
  const [y, m, d] = monday.split("-").map(Number);
  const local = new Date(y!, (m ?? 1) - 1, d ?? 1);
  return formatWeekRange(local);
}

/**
 * The full week (Monday..Sunday) `weekOffset` weeks from the week containing
 * `now` — the single entry point the planner UI needs: `monday` (the API's
 * `weekStart`), the 7 `days` (each flagged `isToday`), and a display label.
 */
export function getWeek(weekOffset: number, now: Date = new Date()): WeekInfo {
  const monday = mondayOfWeek(now, weekOffset);
  const today = localMidnight(now);

  const days: WeekDay[] = DAY_NAMES.map((label, dayIndex) => {
    const d = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + dayIndex);
    return {
      date: toLocalDateString(d),
      dayIndex,
      label,
      isToday: d.getTime() === today.getTime(),
    };
  });

  return {
    monday: toLocalDateString(monday),
    days,
    rangeLabel: formatWeekRange(monday),
  };
}
